import axios from 'axios';
import api from 'src/config/api';
import giphyAPI from 'src/config/giphy';
import { bytesToSize } from 'src/utils/bytes';
import {
  FileTypes,
  Attachment,
  FormFile,
  SignedUrlResponse,
} from 'src/types/attachments';
import {
  getAttachment,
  getBlob,
  getBlobUrl,
  getSimplifiedFileName,
} from 'src/utils/files';
import 'src/components/attachment/i18n';

const base64js = require('base64-js');

export const fileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

export const getDimensions = (
  file,
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onerror = error => reject(error);
    reader.onload = theFile => {
      const image = new Image();
      image.src = theFile.target.result as string;
      image.onload = () => {
        resolve({
          width: image.width,
          height: image.height,
        });
      };
    };
  });

export const getBinaryDataFromUri = async uri => {
  const base64 = uri.split(',')[1];
  return base64js.toByteArray(base64);
};

export const getGif = gifId => giphyAPI.gif(gifId);

export const getTrendingGifs = params => giphyAPI.trending(params);

export const searchGifs = (searchTerm, params) =>
  giphyAPI.search(searchTerm, params);

export const uploadFileToS3 = async (file, signedUrl: string) => {
  const data = await getBinaryDataFromUri(file.uri);

  const config = {
    headers: {
      'Content-Type': file.mime,
      'Content-Encoding': 'base64',
    },
  };

  const response = await axios.put(signedUrl, data, config);

  return response;
};

const getFile = file => ({
  uri: file.uri,
  name: file.name,
  type: file.type,
  mime: file.mime,
  size: file.size,
});

export const getSignedUrl = (fileName, type, contentType) =>
  api.get<SignedUrlResponse>('/signed-url', {
    params: {
      fileName,
      type,
      contentType,
    },
  });

export const signUrl = async (
  name: string,
  type: string,
  mime: string,
): Promise<string> => {
  const response = await getSignedUrl(
    getSimplifiedFileName(name, type),
    type,
    mime,
  );

  return response?.data?.url;
};

export const uploadAudio = async audio => {
  const signedUrl = await signUrl(audio.name, FileTypes.AUDIO, audio.mime);

  await uploadFileToS3(audio, signedUrl);

  return {
    url: signedUrl.split('?')[0],
    type: FileTypes.AUDIO,
    name: audio.name,
    size: audio.size,
    width: audio.width,
  };
};

export const uploadImage = async (image: FormFile): Promise<Attachment> => {
  let uri = null;

  uri = await fileToBase64(image.fileObject);

  const signedUrl = await signUrl(image.name, FileTypes.IMAGE, image.mime);

  await uploadFileToS3(getFile({ ...image, uri }), signedUrl);

  return {
    url: signedUrl.split('?')[0],
    type: FileTypes.IMAGE,
    name: image.name,
    width: image?.width,
    height: image?.height,
    position: image?.position,
    size: image.fileObject && bytesToSize(image.fileObject.size),
  };
};

export const uploadFile = async (file: FormFile): Promise<Attachment> => {
  const uri = await fileToBase64(file.fileObject);
  const signedUrl = await signUrl(file.name, FileTypes.FILE, file.mime);

  await uploadFileToS3(getFile({ ...file, uri }), signedUrl);

  return {
    url: signedUrl.split('?')[0],
    type: FileTypes.FILE,
    name: file.name,
    width: file?.width || null,
    height: file?.height || null,
    position: null,
    size: file.fileObject && bytesToSize(file.fileObject.size),
  };
};

export const uploadVideo = async (video: FormFile): Promise<Attachment> => {
  const uri = await fileToBase64(video.fileObject);
  const signedUrl = await signUrl(video.name, FileTypes.VIDEO, video.mime);

  await uploadFileToS3(getFile({ ...video, uri }), signedUrl);

  return {
    url: signedUrl.split('?')[0],
    type: FileTypes.VIDEO,
    name: video.name,
    width: video.width || null,
    height: video.height || null,
    position: video?.position,
    size: video.fileObject && bytesToSize(video.fileObject.size),
  };
};

export const uploadAllFiles = (files: FormFile[]): Promise<Attachment[]> =>
  uploadFormFiles(files, uploadFile);

export const uploadAllImages = (images: FormFile[]): Promise<Attachment[]> =>
  uploadFormFiles(images, uploadImage);

export const uploadAllVideos = (videos: FormFile[]): Promise<Attachment[]> =>
  uploadFormFiles(videos, uploadVideo);

export const uploadAllAttachments = (
  files: FormFile[],
  images: FormFile[],
  videos: FormFile[],
) =>
  Promise.all([
    uploadAllFiles(files),
    uploadAllImages(images),
    uploadAllVideos(videos),
  ]);

export const uploadFormFiles = async (
  files: FormFile[],
  upload: (files: FormFile) => Promise<Attachment>,
): Promise<Attachment[]> => {
  const toUpload = files.filter(file => file.toUpload);
  const toNotUpload = files.filter(file => !file.toUpload);

  const uploadedFiles = await Promise.all(toUpload.map(upload));
  const notUploadedFiles = toNotUpload.map(getAttachment).map(file => ({
    ...file,
    name: file.name,
    url: file.url && file.url.split('?')[0],
  }));

  return [...notUploadedFiles, ...uploadedFiles];
};

export const fetchBlob = async (attachment: Attachment) => {
  const blob = await getBlob(attachment?.url);
  const blobUrl = getBlobUrl(blob);

  return {
    blob,
    blobUrl,
  };
};
